import React, { useState } from 'react';
import { Button } from './ui/button';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

// Fenetre de changement de mot de passe pour un utilisateur deja connecte.
// Ajout au parcours existant : un administrateur peut toujours attribuer ou
// reinitialiser un mot de passe depuis la gestion des utilisateurs.
const MIN_LEN = 8;

const ChangePassword = ({ open, onClose }) => {
  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [enCours, setEnCours] = useState(false);

  if (!open) return null;

  const tropCourt = nouveau.length > 0 && nouveau.length < MIN_LEN;
  const discordant = confirmation.length > 0 && nouveau !== confirmation;
  const identique = nouveau.length > 0 && nouveau === actuel;
  const peutValider =
    actuel.length > 0 &&
    nouveau.length >= MIN_LEN &&
    nouveau === confirmation &&
    !identique &&
    !enCours;

  const fermer = () => {
    setActuel('');
    setNouveau('');
    setConfirmation('');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!peutValider) return;
    setEnCours(true);
    try {
      await api.changeOwnPassword(actuel, nouveau);
      toast({
        title: 'Mot de passe modifié',
        description: 'Utilisez le nouveau mot de passe à votre prochaine connexion.',
      });
      fermer();
    } catch (err) {
      toast({
        title: 'Modification refusée',
        description: err?.message || "Le mot de passe actuel n'est pas correct.",
        variant: 'destructive',
      });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-700">
          <div>
            <h2 className="text-white text-lg font-semibold">Changer mon mot de passe</h2>
            <p className="text-slate-400 text-sm mt-1">
              Choisissez un mot de passe d'au moins {MIN_LEN} caractères, connu de vous seul.
            </p>
          </div>
          <button
            type="button"
            onClick={fermer}
            className="text-slate-400 hover:text-white ml-4"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <Label htmlFor="mdp-actuel" className="text-slate-300">Mot de passe actuel</Label>
            <PasswordInput
              id="mdp-actuel"
              value={actuel}
              onChange={(e) => setActuel(e.target.value)}
              placeholder="••••••••"
              required
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 mt-1"
            />
          </div>
          <div>
            <Label htmlFor="mdp-nouveau" className="text-slate-300">Nouveau mot de passe</Label>
            <PasswordInput
              id="mdp-nouveau"
              value={nouveau}
              onChange={(e) => setNouveau(e.target.value)}
              placeholder="••••••••"
              required
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 mt-1"
            />
            {tropCourt && (
              <p className="text-amber-400 text-xs mt-1">
                Encore {MIN_LEN - nouveau.length} caractère(s).
              </p>
            )}
            {identique && (
              <p className="text-amber-400 text-xs mt-1">
                Le nouveau mot de passe doit être différent de l'actuel.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="mdp-confirmation" className="text-slate-300">Confirmer</Label>
            <PasswordInput
              id="mdp-confirmation"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="••••••••"
              required
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 mt-1"
            />
            {discordant && (
              <p className="text-red-400 text-xs mt-1">
                Les deux saisies ne correspondent pas.
              </p>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              disabled={!peutValider}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {enCours ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button
              type="button"
              onClick={fermer}
              className="bg-slate-700 hover:bg-slate-600 text-slate-200"
            >
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
